import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { toast } from 'sonner'
import { Minus, Plus } from 'lucide-react'

import { useCreateInquiryEvent } from '@/stores/leads.store'
import { useResolveLocationTimezone } from '@/stores/events.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { combineDateAndTime } from '@/lib/inquiry-utils'
import { useEventTypes } from '@/hooks/use-event-types'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { DurationInput } from '@/components/shared/duration-input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { InputGroup } from '@/components/ui/input-group'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { BaristaCartCountFields } from '@/components/shared/barista-cart-count-fields'
import { TimezoneSelect } from '@/components/shared/timezone-select'
import { LocationTimezoneSync } from '@/components/shared/location-timezone-sync'

interface InquiryEventCreateSheetProps {
  inquiryId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultEventLength?: number
  defaultEventGuests?: number
  defaultEventTime?: string
  defaultBaristaCount?: number
  defaultCartCount?: number
  defaultEventType?: string
  defaultEventLocation?: string
  defaultEventTimezone?: string
}

/**
 * InquiryEventCreateSheet - Sheet component for creating new inquiry events
 *
 * Features:
 * - Enter event date, duration (hours), and guest count
 * - Uses TanStack Form for form state management
 * - Leverages useCreateInquiryEvent hook for creating events
 * - Displays loading states during mutation
 * - Shows toast notifications on success/error
 * - Resets form on close
 *
 * @param inquiryId - The ID of the parent inquiry
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function InquiryEventCreateSheet({
  inquiryId,
  open,
  onOpenChange,
  defaultEventLength,
  defaultEventGuests,
  defaultEventTime,
  defaultBaristaCount,
  defaultCartCount,
  defaultEventType,
  defaultEventLocation,
  defaultEventTimezone,
}: InquiryEventCreateSheetProps) {
  const { createEvent, isLoading } = useCreateInquiryEvent()
  const { resolve: resolveTimezoneFromLocation } = useResolveLocationTimezone()

  // Archived types are not assignable to new records, but the one already
  // on this record stays selectable so the field isn't blank.
  const { getEventTypeOptions } = useEventTypes()

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      eventDate: undefined as Date | undefined,
      eventTime: defaultEventTime ?? '12:00',
      eventLength: defaultEventLength ?? 0,
      eventGuests: defaultEventGuests ?? 0,
      baristaCount: defaultBaristaCount ?? 1,
      cartCount: defaultCartCount ?? 1,
      eventType: defaultEventType ?? '',
      eventLocation: defaultEventLocation ?? '',
      eventTimezone: defaultEventTimezone ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        let eventDate = undefined
        if (value.eventDate) {
          const combinedDate = combineDateAndTime(value.eventDate, value.eventTime)
          eventDate = dateToTimestamp(combinedDate)
        }

        await createEvent({
          inquiryId,
          eventDate,
          eventLength: value.eventLength || undefined,
          eventGuests: value.eventGuests || undefined,
          baristaCount: value.baristaCount ?? undefined,
          cartCount: value.cartCount ?? undefined,
          eventType: value.eventType || undefined,
          eventLocation: value.eventLocation.trim() || undefined,
          eventTimezone: value.eventTimezone || undefined,
        })

        toast.success('Event added')
        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error('Failed to create event:', error)
        reportError(error, { feature: 'inquiry_event_create', kind: 'mutation' })
        toast.error('Failed to add event', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
    }
    onOpenChange(newOpen)
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add Event</SheetTitle>
          <SheetDescription>
            Enter the event details. Click add when you&apos;re done.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="inquiry-event-create-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
            <div className="grid gap-3">
              <Label>Event Date & Time</Label>
              <div className="grid grid-cols-2 gap-2">
                <form.Field name="eventDate">
                  {(field) => (
                    <DatePicker
                      value={field.state.value}
                      onChange={(date) => field.handleChange(date)}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
                <form.Field name="eventTime">
                  {(field) => (
                    <TimePicker
                      value={field.state.value}
                      onChange={(time) => field.handleChange(time)}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
              </div>
            </div>

            <form.Subscribe selector={(s) => s.values.eventLocation}>
              {(eventLocation) => (
                <form.Field name="eventTimezone">
                  {(field) => {
                    const trimmedLocation = (eventLocation ?? '').trim()
                    return (
                      <div className="grid gap-1">
                        <TimezoneSelect
                          id="event-timezone"
                          label="Timezone"
                          value={field.state.value || undefined}
                          onChange={(value) => field.handleChange(value)}
                          disabled={isLoading}
                          onResolveFromLocation={() => resolveTimezoneFromLocation(trimmedLocation)}
                          resolveDisabledReason={trimmedLocation ? undefined : 'Set an event location first'}
                        />
                        <p className="text-xs text-muted-foreground">
                          Follows the event location automatically. Pick a zone here to override it.
                        </p>
                      </div>
                    )
                  }}
                </form.Field>
              )}
            </form.Subscribe>

            <form.Field name="eventLength">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-length">Duration</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <DurationInput
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={isLoading}
                      min={0}
                      step={0.25}
                    />
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="eventGuests">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-guests">Guest Count</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-r border-input"
                      onClick={() => {
                        const newValue = Math.max(0, field.state.value - 25)
                        field.handleChange(newValue)
                      }}
                      disabled={isLoading || field.state.value <= 0}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease by 25</span>
                    </Button>
                    <Input
                      id="event-guests"
                      type="number"
                      min="0"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const value = Number(e.target.value) || 0
                        const clampedValue = Math.max(0, value)
                        field.handleChange(clampedValue)
                        field.handleBlur()
                      }}
                      disabled={isLoading}
                      placeholder="0"
                      className="border-0 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-l border-input"
                      onClick={() => {
                        const newValue = field.state.value + 25
                        field.handleChange(newValue)
                      }}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase by 25</span>
                    </Button>
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="baristaCount">
              {(baristaField) => (
                <form.Field name="cartCount">
                  {(cartField) => (
                    <BaristaCartCountFields
                      baristaCount={baristaField.state.value}
                      cartCount={cartField.state.value}
                      onBaristaCountChange={baristaField.handleChange}
                      onCartCountChange={cartField.handleChange}
                      onBaristaCountBlur={baristaField.handleBlur}
                      onCartCountBlur={cartField.handleBlur}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
              )}
            </form.Field>

            <form.Field name="eventType">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-type">Event Type</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={isLoading}
                  >
                    <SelectTrigger id="event-type" className="w-full">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {getEventTypeOptions(field.state.value).map((eventType) => (
                        <SelectItem key={eventType.key} value={eventType.key}>
                          {eventType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="eventLocation">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-location">Location</Label>
                  <InputGroup>
                    <PlacesAutocomplete
                      id="event-location"
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter event location"
                      disabled={isLoading}
                    />
                  </InputGroup>
                  <form.Subscribe selector={(s) => s.values.eventTimezone}>
                    {(eventTimezone) => (
                      <LocationTimezoneSync
                        location={field.state.value ?? ''}
                        timezone={eventTimezone ?? ''}
                        onTimezoneChange={(timezone) => form.setFieldValue('eventTimezone', timezone)}
                        resolve={resolveTimezoneFromLocation}
                      />
                    )}
                  </form.Subscribe>
                </div>
              )}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </SheetClose>
          <Button type="submit" form="inquiry-event-create-form" disabled={isLoading}>
            {isLoading ? 'Adding...' : 'Add Event'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
